import { FilterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  Form,
  Input,
  InputNumber,
  Button,
  type FormProps,
  Flex,
  theme,
  Radio,
  DatePicker,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CrudFilters } from "@refinedev/core";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { opcionesDeMatch, tramoDelFiltro } from "./filtroDeMatch";

const { RangePicker } = DatePicker;

export const ProductsFilters: React.FC<{
  searchFormProps: FormProps;
  filters?: CrudFilters;
  sellersSelectProps?: any;
  storesSelectProps?: any;
  t?: any;
  /** Productos sin el % de match calculado todavía (xcons-docs#624). */
  matchPending?: number;
}> = ({
  searchFormProps,
  filters,
  sellersSelectProps,
  storesSelectProps,
  t,
  matchPending,
}) => {
  const [calendarValue, setCalendarValue] = useState<any>();
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [radioValue, setRadioValue] = useLocalStorage<any>("RadioCalendar", []);
  const [calendarValueCreated, setCalendarValueCreated] = useState<any>();
  const [showRangePickerCreated, setShowRangePickerCreated] = useState(false);
  const [radioValueCreated, setRadioValueCreated] = useLocalStorage<any>(
    "RadioCalendarCreated",
    [],
  );

  const { token } = theme.useToken();

  const matchAplicado = tramoDelFiltro(
    // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
    filters?.find((f) => f.field === "matchScore" && f.value !== undefined)?.value,
  );
  const [tramoDeMatch, setTramoDeMatch] = useState(matchAplicado.tramo);

  const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      searchFormProps.form?.setFieldsValue({ calendar: dates });
    } else {
      searchFormProps.form?.setFieldsValue({ calendar: undefined });
    }
  };

  const handleRangeChangeCreated = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      searchFormProps.form?.setFieldsValue({ calendar_created: dates });
    } else {
      searchFormProps.form?.setFieldsValue({ calendar_created: undefined });
    }
  };

  useEffect(() => {
    searchFormProps.form?.setFieldsValue({ calendar: calendarValue });
  }, [calendarValue]);

  useEffect(() => {
    searchFormProps.form?.setFieldsValue({
      calendar_created: calendarValueCreated,
    });
  }, [calendarValueCreated]);

  const thisWeek: [Dayjs, Dayjs] = [
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ];
  const thisMonth: [Dayjs, Dayjs] = [
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ];

  const rangePresets = useMemo(
    () => [
      { label: t("filter.calendar.all"), value: "" },
      { label: t("filter.calendar.thisWeek"), value: "thisweek" },
      { label: t("filter.calendar.thisMonth"), value: "thismonth" },
      { label: t("filter.calendar.personalized"), value: "personalized" },
    ],
    [t],
  );

  useEffect(() => {
    if (filters) {
      /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
      const calendarFilter = filters?.find((f) => f.field === "updatedAt");
      const calendarFilterCreated = filters?.find(
        /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
        (f) => f.field === "createdAt",
      );

      if (calendarFilter) {
        const [start, end] = calendarFilter.value || [];
        if (start && end) {
          setCalendarValue([dayjs(start), dayjs(end)]);
        } else {
          setCalendarValue(undefined);
          setRadioValue("");
          setShowRangePicker(false);
        }
      }

      if (calendarFilterCreated) {
        const [start, end] = calendarFilterCreated.value || [];
        if (start && end) {
          setCalendarValueCreated([dayjs(start), dayjs(end)]);
        } else {
          setCalendarValueCreated(undefined);
          setRadioValueCreated("");
          setShowRangePickerCreated(false);
        }
      }
    }
  }, [filters]);

  const handleRadioChange = useCallback(
    (e: any) => {
      const { value } = e.target;
      setRadioValue(value);

      if (value === "personalized") {
        setShowRangePicker(true);
        setCalendarValue(value);
      } else if (value === "thisweek") {
        setShowRangePicker(false);
        setCalendarValue(thisWeek);
      } else if (value === "thismonth") {
        setShowRangePicker(false);
        setCalendarValue(thisMonth);
      } else {
        setShowRangePicker(false);
        setCalendarValue(undefined);
      }
    },
    [setRadioValue, setShowRangePicker, setCalendarValue],
  );

  const handleRadioChangeCreated = useCallback(
    (e: any) => {
      const { value } = e.target;
      setRadioValueCreated(value);

      if (value === "personalized") {
        setShowRangePickerCreated(true);
        setCalendarValueCreated(value);
      } else if (value === "thisweek") {
        setShowRangePickerCreated(false);
        setCalendarValueCreated(thisWeek);
      } else if (value === "thismonth") {
        setShowRangePickerCreated(false);
        setCalendarValueCreated(thisMonth);
      } else {
        setShowRangePickerCreated(false);
        setCalendarValueCreated(undefined);
      }
    },
    [setRadioValueCreated, setShowRangePickerCreated, setCalendarValueCreated],
  );

  function clearFilters() {
    searchFormProps.form?.resetFields();
    setCalendarValue(undefined);
    setCalendarValueCreated(undefined);
    setRadioValue("");
    setRadioValueCreated("");
    setShowRangePicker(false);
    setTramoDeMatch(undefined);
    searchFormProps.form?.submit();
  }

  return (
    <Form layout="vertical" {...searchFormProps} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      {/* Primero y con botones, como Fecha de creación (xcons-docs#609): es el filtro que más
          se usa, y con dos opciones no hace falta abrir nada. */}
      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelStatusCatalog")}
          </span>
        }
        name="status"
        // Sin filtro, "Todos" marcado: el form guarda nada y el botón muestra "".
        getValueProps={(v) => ({ value: v ?? "" })}
        normalize={(v) => v || undefined}
      >
        <Radio.Group
          size="small"
          // Separados, como los de las fechas.
          style={{ display: "flex", gap: "10px" }}
        >
          <Radio.Button value="">{t("filter.calendar.all")}</Radio.Button>
          <Radio.Button value="linked">
            {t("filter.products.optionLinked")}
          </Radio.Button>
          <Radio.Button value="unlinked">
            {t("filter.products.optionUnlinked")}
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      {/* El % de la sugerencia, guardado: filtrar no calcula nada (xcons-docs#624). Un producto
          que todavía no se calculó no entra, y se avisa cuántos son. */}
      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelMatch")}
          </span>
        }
        name="match_tramo"
        // Sin filtro, "Todos" marcado: el form guarda nada y el botón muestra "".
        getValueProps={(v) => ({ value: v ?? "" })}
        normalize={(v) => v || undefined}
        extra={
          matchPending
            ? t("filter.products.matchPending", { count: matchPending })
            : undefined
        }
      >
        {/* Con botones, como Fecha de creación (xcons-docs#609): Personalizado abre desde y
            hasta abajo. */}
        <Radio.Group
          size="small"
          // Separados, como los de las fechas.
          style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
          onChange={(e) => {
            const value = e.target.value || undefined;
            setTramoDeMatch(value);
            searchFormProps.form?.setFieldsValue({ match_tramo: value });
          }}
        >
          <Radio.Button value="">{t("filter.calendar.all")}</Radio.Button>
          {opcionesDeMatch(t).map((o) => (
            <Radio.Button key={o.value} value={o.value}>
              {o.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>

      {tramoDeMatch === "personalizado" && (
        <Flex gap={10}>
          <Form.Item
            label={
              <span style={{ color: token.colorTextTertiary }}>
                {t("filter.products.matchFrom")}
              </span>
            }
            name="match_desde"
            style={{ flex: 1 }}
          >
            <Flex vertical>
              <InputNumber
                min={0}
                max={100}
                addonAfter="%"
                defaultValue={matchAplicado.desde}
                onChange={(value) =>
                  searchFormProps.form?.setFieldsValue({
                    match_desde: value ?? undefined,
                  })
                }
              />
            </Flex>
          </Form.Item>
          <Form.Item
            label={
              <span style={{ color: token.colorTextTertiary }}>
                {t("filter.products.matchTo")}
              </span>
            }
            name="match_hasta"
            style={{ flex: 1 }}
          >
            <Flex vertical>
              <InputNumber
                min={0}
                max={100}
                addonAfter="%"
                defaultValue={matchAplicado.hasta}
                onChange={(value) =>
                  searchFormProps.form?.setFieldsValue({
                    match_hasta: value ?? undefined,
                  })
                }
              />
            </Flex>
          </Form.Item>
        </Flex>
      )}

      {/* Tercero (xcons-docs#609): después de Asociado y del Match. */}
      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelStatus")}
          </span>
        }
        name="status_product"
        // Botones como Condición y Match (xcons-docs#609). 0 es Inactivo: no se normaliza a vacío.
        initialValue={
          // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
          filters?.find((f) => f.field === "variations.enabled")?.value
        }
        getValueProps={(v) => ({ value: v ?? "" })}
        normalize={(v) => (v === "" ? undefined : v)}
      >
        <Radio.Group size="small" style={{ display: "flex", gap: "10px" }}>
          <Radio.Button value="">{t("filter.calendar.all")}</Radio.Button>
          <Radio.Button value={1}>{t("filter.products.labelStatusActive")}</Radio.Button>
          <Radio.Button value={0}>{t("filter.products.labelStatusDesactive")}</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelBrand")}
          </span>
        }
        name="brands_product"
        rules={[{ whitespace: true, message: t("message.error.inputEmpty") }]}
      >
        <Flex vertical>
          <Input
            type="text"
            defaultValue={
              /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
              filters?.find((f) => f.field === "manufacturer")?.value
            }
          />
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelManufacturerSku")}
          </span>
        }
        name="manufacture_sku"
        rules={[{ whitespace: true, message: t("message.error.inputEmpty") }]}
      >
        <Flex vertical>
          <Input
            type="text"
            defaultValue={
              /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
              filters?.find((f) => f.field === "variations.mainCatalog.sku")
                ?.value
            }
          />
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelSku")}
          </span>
        }
        name="catalog_sku"
        rules={[{ whitespace: true, message: t("message.error.inputEmpty") }]}
      >
        <Flex vertical>
          <Input
            type="text"
            /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
            defaultValue={filters?.find((f) => f.field === "sku")?.value}
          />
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelName")}
          </span>
        }
        name="name_product"
        rules={[{ whitespace: true, message: t("message.error.inputEmpty") }]}
      >
        <Flex vertical>
          <Input
            type="text"
            defaultValue={searchFormProps.form?.getFieldValue("name_product")}
          />
        </Flex>
      </Form.Item>

      <Flex gap={8}>
        <Form.Item
          label={
            <span style={{ color: token.colorTextTertiary }}>
              {t("filter.products.labelPrice")}
            </span>
          }
          name="price_product_from"
        >
          <InputNumber<number>
            addonBefore="$"
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceFrom")}
          />
        </Form.Item>
        <Form.Item
          label={
            <span style={{ color: "white" }}>
              {t("filter.products.labelPrice")}
            </span>
          }
          name="price_product_to"
        >
          <InputNumber<number>
            addonBefore="$"
            min={0}
            style={{ width: "100%" }}
            placeholder={t("filter.products.labelPriceTo")}
          />
        </Form.Item>
      </Flex>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.products.labelFeatured")}
          </span>
        }
        name="featured_product"
        // Sí / No, no Activo / Inactivo, que es lo que dice Estado (xcons-docs#609).
        initialValue={
          // @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66
          filters?.find((f) => f.field === "featured")?.value
        }
        getValueProps={(v) => ({ value: v ?? "" })}
        normalize={(v) => (v === "" ? undefined : v)}
      >
        <Radio.Group size="small" style={{ display: "flex", gap: "10px" }}>
          <Radio.Button value="">{t("filter.calendar.all")}</Radio.Button>
          <Radio.Button value={1}>{t("filter.products.featuredYes")}</Radio.Button>
          <Radio.Button value={0}>{t("filter.products.featuredNo")}</Radio.Button>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.calendar.lastUpdate")}
          </span>
        }
        name="calendar"
        key="calendar_updated"
      >
        <Flex vertical>
          <Radio.Group
            value={radioValue}
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: showRangePicker ? "10px" : "",
            }}
            size="small"
            onChange={handleRadioChange}
          >
            {rangePresets.map((preset, i) => (
              <Radio.Button key={i} value={preset.value} name={preset.value}>
                {preset.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          {showRangePicker && (
            <RangePicker showTime onChange={handleRangeChange} required />
          )}
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.calendar.lastCreate")}
          </span>
        }
        name="calendar_created"
        key="calendar_created_unique"
      >
        <Flex vertical>
          <Radio.Group
            value={radioValueCreated}
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: showRangePickerCreated ? "10px" : "",
            }}
            size="small"
            onChange={handleRadioChangeCreated}
          >
            {rangePresets.map((preset, i) => (
              <Radio.Button
                key={`${i}-created`}
                value={preset.value}
                name={preset.value}
              >
                {preset.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          {showRangePickerCreated && (
            <RangePicker
              showTime
              onChange={handleRangeChangeCreated}
              required
            />
          )}
        </Flex>
      </Form.Item>

      </div>
      <Flex
        align="center"
        justify="end"
        gap="8px"
        style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
      >
        <Button type="default" danger onClick={clearFilters}>
          {t("filter.button.clearFilters")}
        </Button>
        <Button htmlType="submit" type="primary" icon={<FilterOutlined />}>
          {t("filter.button.filter")}
        </Button>
      </Flex>
    </Form>
  );
};
